/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { z } from 'zod';
import {
  TRANSACTION_TO_SETTINGS_KEY,
  type PropertyRequiredFields,
} from '@features/property-management/hooks/usePropertySettings';
import { getCategoryConfig } from './schema.category-config';
import { NO_CONSTRUCTED_AREA_CATEGORIES } from './schema.constants';

/** What the agency requires on top of the form's own floor, and how to say so. */
export interface AgencyRequiredRule {
  required: PropertyRequiredFields;
  message: string;
}

/** The form values that decide which required fields apply. */
export interface AgencyRequiredContext {
  transaction_type?: string[];
  category?: string;
  condominium?: boolean;
}

/**
 * Counts the broker picks from 0 to 5+. They open with nothing picked, so 0
 * is an answer (a studio has no bedroom) and only "nothing picked" is blank.
 */
const COUNT_FIELDS: ReadonlySet<string> = new Set(['bedrooms', 'suites', 'bathrooms', 'parking_spots']);

/**
 * When a field is on screen. A required field the form hides could never be
 * filled, so it stops being required while hidden.
 */
const SHOWN_WHEN: Record<string, (ctx: AgencyRequiredContext) => boolean> = {
  sale_price: (ctx) => !!ctx.transaction_type?.includes('Venda'),
  rental_price: (ctx) => !!ctx.transaction_type?.includes('Aluguel'),
  condominium_price: (ctx) => ctx.condominium === true,
  constructed_area: (ctx) => !(NO_CONSTRUCTED_AREA_CATEGORIES as readonly string[]).includes(ctx.category ?? ''),
};

/**
 * Whether a field is on screen for this property.
 *
 * @param field - The form field key.
 * @param ctx - The values that decide what the form shows.
 * @returns True when the broker can see and fill the field.
 */
function isShown(field: string, ctx: AgencyRequiredContext): boolean {
  if (COUNT_FIELDS.has(field)) {
    return (getCategoryConfig(ctx.category ?? '').characteristics as string[]).includes(field);
  }
  return SHOWN_WHEN[field]?.(ctx) ?? true;
}

/** How a non-null value of each type reads as blank. */
const BLANK_BY_TYPE: Partial<Record<string, (field: string, value: unknown) => boolean>> = {
  string: (_field, value) => (value as string).trim() === '',
  number: (field, value) => Number.isNaN(value) || (value === 0 && !COUNT_FIELDS.has(field)),
  object: (_field, value) => Array.isArray(value) && value.length === 0,
};

/**
 * Whether a parsed form value counts as not filled in.
 *
 * A money or area of 0 is blank: an emptied masked input parses to 0, and
 * the request drops a 0 price. A count of 0 is filled (see COUNT_FIELDS).
 *
 * @param field - The form field key.
 * @param value - The parsed value.
 * @returns True when the broker has not filled it.
 */
function isBlank(field: string, value: unknown): boolean {
  if (value == null) return true;
  return BLANK_BY_TYPE[typeof value]?.(field, value) ?? false;
}

/**
 * The fields the agency requires of this property right now: the lists of
 * its transactions, less the fields the form is not showing. A property for
 * sale and rent answers to both lists; one with no transaction to neither.
 *
 * @param required - The agency's lists.
 * @param ctx - The values that decide which lists and fields apply.
 * @returns The union of the lists that apply, shown fields only.
 */
export function fieldsRequiredFor(
  required: PropertyRequiredFields,
  ctx: AgencyRequiredContext,
): string[] {
  const keys = (ctx.transaction_type ?? [])
    .map((tx) => TRANSACTION_TO_SETTINGS_KEY[tx])
    .filter(Boolean);
  const fields = new Set(keys.flatMap((key) => required[key] ?? []));
  return [...fields].filter((field) => isShown(field, ctx));
}

/**
 * Builds the form-level check of the agency's required fields.
 *
 * @param rule - The agency's lists and the message for a blank field.
 * @returns A zod `superRefine` callback.
 */
export function agencyRequiredRefinement(rule: AgencyRequiredRule) {
  return (values: Record<string, unknown>, ctx: z.RefinementCtx) => {
    for (const field of fieldsRequiredFor(rule.required, values as AgencyRequiredContext)) {
      if (isBlank(field, values[field])) {
        ctx.addIssue({ code: 'custom', path: [field], message: rule.message });
      }
    }
  };
}
